// Prism: Website-Feld prüfen (16.10.2026).
//
// Das Feld fragt nach der EIGENEN Firmen-Website, damit die KI versteht, was der Kunde
// verkauft. 8 von 48 Läufen (01.–15.10.) trugen dort leadscraper.de, einen ChatGPT- oder
// einen Instagram-Link ein; die Analyse las dann unser eigenes Angebot oder eine leere
// Social-Seite. Eine Regel für Funnel (freundlicher Hinweis) und API (solche Adressen gar
// nicht erst analysieren), damit beide dasselbe abfangen.

/**
 * Warum eine Adresse nicht die eigene Firmen-Website ist:
 * - `own_product`: leadscraper selbst (die Seite, auf der man gerade ist)
 * - `ai_chat`: Link zu einem KI-Chat (chatgpt.com, claude.ai, …)
 * - `social`: Profil auf einer Plattform (Instagram, LinkedIn, Facebook, …)
 * - `redirect`: Kurz- oder Weiterleitungslink (bit.ly, linktr.ee, google.com/url, …)
 */
export type PrismWebsiteIssue = 'own_product' | 'ai_chat' | 'social' | 'redirect';

const OWN_PRODUCT = ['leadscraper.de', 'leadscraper.com', 'leadscraper.io', 'deimann.io'];
const AI_CHAT = [
  'chatgpt.com',
  'chat.openai.com',
  'openai.com',
  'claude.ai',
  'gemini.google.com',
  'perplexity.ai',
  'copilot.microsoft.com',
  'chat.mistral.ai',
  'poe.com',
  'you.com',
];
const SOCIAL = [
  'instagram.com',
  'facebook.com',
  'fb.com',
  'm.facebook.com',
  'linkedin.com',
  'xing.com',
  'tiktok.com',
  'twitter.com',
  'x.com',
  'youtube.com',
  'youtu.be',
  'pinterest.com',
  'pinterest.de',
  'threads.net',
  'snapchat.com',
  'wa.me',
  'whatsapp.com',
  't.me',
  'telegram.me',
];
const REDIRECT = [
  'bit.ly',
  't.co',
  'tinyurl.com',
  'goo.gl',
  'ow.ly',
  'lnkd.in',
  'buff.ly',
  'is.gd',
  'rebrand.ly',
  'shorturl.at',
  'cutt.ly',
  'linktr.ee',
  'linkin.bio',
  'beacons.ai',
  'l.instagram.com',
  'l.facebook.com',
  'lm.facebook.com',
  'out.reddit.com',
  'g.page',
  'maps.app.goo.gl',
];

/** Hostname aus einer frei getippten Adresse („www.firma.de/kontakt“, „https://…“). */
export function prismWebsiteHost(input: string): string {
  // Bewusst ohne URL-Klasse: das Schema-Paket läuft ohne DOM-/Node-Typen.
  const raw = (input || '').trim().toLowerCase();
  if (!raw) return '';
  const host = raw
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/^[^@/?#]*@/, '') // user:pass@host
    .split(/[/?#:\s\\]/)[0];
  return (host ?? '').replace(/^www\./, '').replace(/\.$/, '');
}

function matches(host: string, list: string[]): boolean {
  return list.some((d) => host === d || host.endsWith(`.${d}`));
}

/**
 * null = sieht nach einer eigenen Website aus (oder ist leer). Sonst der Grund, warum die
 * Adresse nicht die eigene Firmen-Website ist.
 */
export function classifyPrismWebsite(input: string): PrismWebsiteIssue | null {
  const host = prismWebsiteHost(input);
  if (!host) return null;
  if (matches(host, OWN_PRODUCT)) return 'own_product';
  if (matches(host, AI_CHAT)) return 'ai_chat';
  if (matches(host, SOCIAL)) return 'social';
  if (matches(host, REDIRECT)) return 'redirect';
  // Google-Weiterleitungen und Google-Maps-Links (google.de/url?…, google.com/maps/…).
  if (/^(?:[a-z]+\.)?google\.[a-z.]+$/.test(host)) {
    const raw = input.toLowerCase();
    if (/\/(?:url|maps|search)\b/.test(raw) || host.startsWith('maps.')) return 'redirect';
  }
  return null;
}
